import './FormField.scss';

import { useFormikContext } from 'formik';
import { camelCase, get } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Form, InputGroup } from 'react-bootstrap';

import type { IForm, IFormField } from '@customTypes/form';
import { camelToTitleCase } from '@helpers/string';

const generateName = (name: string, prefix = ''): string => {
  return prefix ? `${prefix}.${name}` : name;
};

export default function FormField<T extends IForm>({
  type = 'text',
  prefix = '',
  prefixIcon,
  name,
  cols,
  className = '',
  label,
  disabled = false,
  required = false,
  helperText = '',
  inputProps = {},
  format,
}: IFormField<T>) {
  const { touched, submitCount, errors, values, handleBlur, setFieldValue } = useFormikContext<T>();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [displayValue, setDisplayValue] = useState('');
  const groupClassName = useMemo(() => {
    if (cols === 4) return 'col-12 col-lg-4';
    if (cols === 6) return 'col-12 col-lg-6';
    if (cols === 8) return 'col-12 col-lg-8';
    return className;
  }, [cols, className]);

  const formControlName = useMemo(() => generateName(name, prefix), [prefix, name]);
  const error = useMemo(() => get(errors, formControlName), [formControlName, errors]);
  const isDirty = useMemo(() => get(touched, formControlName), [formControlName, touched]);
  const fieldValue = useMemo(() => get(values, formControlName), [values, formControlName]);

  useEffect(() => {
    if (format === 'currency' && fieldValue !== undefined && fieldValue !== null) {
      const number = Number(fieldValue);
      setDisplayValue(isNaN(number) || !fieldValue ? '' : number.toLocaleString('en-US'));
    } else {
      setDisplayValue(fieldValue ?? '');
    }
  }, [fieldValue, format]);

  const parseCurrency = (val: string) => val.replace(/[^0-9]/g, '');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const rawByType = type === 'number' ? e.target.valueAsNumber : raw;

    if (format === 'currency') {
      const parsed = parseCurrency(raw);
      setFieldValue(formControlName, Number(parsed));
      const number = Number(parsed);
      setDisplayValue(isNaN(number) || !parsed ? '' : number.toLocaleString('en-US'));
    } else {
      setFieldValue(formControlName, rawByType);
      setDisplayValue(raw);
    }
  };

  return (
    <Form.Group as={Col} controlId={`input_${formControlName}`} className={`${groupClassName} form-field`}>
      <Form.Label className={`${required ? 'required' : ''} fs-xs fw-bold`}>
        {label ?? camelToTitleCase(camelCase(name))}
      </Form.Label>
      <InputGroup>
        {!!prefixIcon && <InputGroup.Text>{prefixIcon}</InputGroup.Text>}
        <Form.Control
          ref={inputRef}
          type={type}
          name={formControlName}
          onBlur={handleBlur}
          onChange={handleChange}
          value={displayValue}
          disabled={disabled}
          aria-describedby={`input_${formControlName}Desc`}
          isValid={isDirty && !error}
          isInvalid={(isDirty || submitCount > 0) && !!error}
          inputMode={format === 'currency' ? 'numeric' : inputProps.inputMode}
          pattern={format === 'currency' ? '[0-9]*' : inputProps.pattern}
          {...inputProps}
        />
      </InputGroup>
      {!!helperText && (
        <Form.Text id={`input_${formControlName}Desc`} className='fs-xs'>
          {helperText}
        </Form.Text>
      )}
      {!!error && (
        <Form.Control.Feedback as='small' role='alert' type='invalid'>
          {error as string}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
}
